import { type ReactNode, type FC } from 'react'
import { twMerge } from 'tailwind-merge'
import cx from 'classnames'
import { type DefaultProps } from '../common'

export interface RadioGroupOption {
  value: string | number | undefined
  label: string
  icon?: ReactNode
  recommended?: boolean
}

export interface RadioGroupProps extends DefaultProps {
  options: RadioGroupOption[]
  name: string
  onChange: (_value: string | number | undefined) => void // This has _ to show it's unused
  value: string | number | undefined
  large?: boolean
  isPII?: boolean
  disabled?: boolean
}

export const RadioGroup: FC<RadioGroupProps> = ({
  options,
  name,
  value,
  onChange,
  className,
  id,
  testId,
  ariaLabel,
  large,
  isPII,
  disabled,
}) => {
  // Component base class names. Can be overridden by className property.
  const baseClass = cx('flex flex-col gap-6 pb-md', {
    'fs-exclude': isPII,
  })

  // Final class names
  const finalClasses = twMerge(baseClass, className)

  return (
    <div
      {...(testId ? { 'data-testid': testId } : {})}
      {...(id ? { id } : {})}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
      className={finalClasses}
    >
      {options.map((option, index) => (
        <label
          key={option.value}
          className={`flex items-center ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'} ${large ? 'pb-md gap-5 border-b border-light' : ' gap-3'}`}
        >
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={value === option.value}
            disabled={disabled}
            onChange={() => {
              if (!disabled) {
                onChange(option.value)
              }
            }}
            className="hidden"
          />
          <div
            className={cx('flex justify-center items-center rounded-full border transition-all size-6', {
              'bg-gray-100 border-gray-300': disabled,
              'bg-white border-btnPrimaryBg': !disabled && value === option.value,
              'border-btnSecondaryBorderDisabled': !disabled && value !== option.value,
            })}
            data-testid={`${testId ?? ''}-value-${String(index + 1)}`}
          >
            {value === option.value && (
              <div
                className={cx('size-3 rounded-full', {
                  'bg-gray-400': disabled,
                  'bg-btnPrimaryBg': !disabled,
                })}
              />
            )}
          </div>
          <div className="flex flex-col justify-center">
            <span
              className={cx('typo-primary-base', {
                'text-gray-400': disabled,
                'text-btnSecondaryTextActive': !disabled,
              })}
              data-testid={`${testId ?? ''}-label-${String(index + 1)}`}
            >
              {(() => {
                // This is needed for the icon to be positioned correctly in edge cases
                const words = option.label.split(' ')
                const allButLast = words.slice(0, -1).join(' ')
                const lastWord = words[words.length - 1]

                return (
                  <>
                    {allButLast ? `${allButLast} ` : null}
                    <span className="relative inline-block">
                      {lastWord}
                      {option.icon ? (
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.preventDefault()
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                            }
                          }}
                          className="absolute -top-0 -right-2 translate-x-full"
                        >
                          {option.icon}
                        </span>
                      ) : null}
                    </span>
                  </>
                )
              })()}
            </span>
            {option.recommended && !disabled ? (
              <span className="text-action typo-primary-xs-bold mt-2xs w-fit border border-action rounded-[16px] px-xs py-3xs">
                RECOMMENDED
              </span>
            ) : null}
          </div>
        </label>
      ))}
    </div>
  )
}
